import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import EditProfileModal from '@/pages/perfil/components/EditProfileModal';
import RegisterBetModal from '@/pages/perfil/components/RegisterBetModal';
import NotificationsModal from '@/pages/perfil/components/NotificationsModal';
import SubscriptionSection from '@/pages/perfil/components/SubscriptionSection';
import LoginStreakBanner from '@/components/feature/LoginStreakBanner';
import AchievementBadges from '@/pages/perfil/components/AchievementBadges';

// ── Excel/CSV export for Elite users ─────────────────────────────────────────
function exportToCSV(rows: Record<string, unknown>[], filename: string) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers.map((h) => {
        const val = String(r[h] ?? '').replace(/"/g, '""');
        return `"${val}"`;
      }).join(',')
    ),
  ].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface PostEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  category_label: string;
  is_prediction: boolean;
  prediction_odds: number | null;
  prediction_market: string | null;
  prediction_result: string;
  likes_count: number;
  views: number;
  created_at: string;
}

type BetEntry = {
  id: string;
  date: string;
  match: string;
  market: string;
  selection: string;
  odds: number;
  stake: number;
  result: 'won' | 'lost' | 'pending';
  profit: number;
  bookmaker: string;
  bet_date?: string;
};

type SupabaseBet = {
  id: string;
  match_name: string;
  market: string;
  selection: string;
  odds: number;
  stake: number;
  result: 'won' | 'lost' | 'pending';
  profit: number;
  bookmaker: string;
  bet_date: string;
  created_at: string;
};

type LoginStreakData = {
  current_streak: number;
  longest_streak: number;
  total_logins: number;
};

function supabaseBetToBetEntry(b: SupabaseBet): BetEntry {
  return {
    id: b.id,
    date: new Date(b.bet_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }),
    match: b.match_name,
    market: b.market,
    selection: b.selection,
    odds: Number(b.odds),
    stake: Number(b.stake),
    result: b.result,
    profit: Number(b.profit),
    bookmaker: b.bookmaker,
    bet_date: b.bet_date,
  };
}

function MiniChart({ history }: { history: { date: string; value: number }[] }) {
  if (history.length < 2) return null;
  const max = Math.max(...history.map((d) => d.value));
  const min = Math.min(...history.map((d) => d.value));
  const range = max - min || 1;
  const w = 100;
  const h = 60;
  const points = history.map((d, i) => {
    const x = (i / (history.length - 1)) * w;
    const y = h - ((d.value - min) / range) * (h - 10) - 5;
    return `${x},${y}`;
  });
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" preserveAspectRatio="none">
      <defs>
        <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00e87a" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#00e87a" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={points.join(' ')} fill="none" stroke="#00e87a" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PerfilPage() {
  const [activeTab, setActiveTab] = useState<'bankroll' | 'posts' | 'estadisticas' | 'suscripcion'>('bankroll');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [registerBetOpen, setRegisterBetOpen] = useState(false);
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [bets, setBets] = useState<BetEntry[]>([]);
  const [betsLoading, setBetsLoading] = useState(false);
  const [userPosts, setUserPosts] = useState<PostEntry[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [updatingPick, setUpdatingPick] = useState<string | null>(null);
  const [deletingPost, setDeletingPost] = useState<string | null>(null);
  const [confirmDeletePostId, setConfirmDeletePostId] = useState<string | null>(null);
  const [confirmDeleteBetId, setConfirmDeleteBetId] = useState<string | null>(null);
  const [deletingBet, setDeletingBet] = useState<string | null>(null);
  const [hideStatsLeagues, setHideStatsLeagues] = useState(false);
  const [updatingHideStats, setUpdatingHideStats] = useState(false);
  const [updatingBetResult, setUpdatingBetResult] = useState<string | null>(null);
  const [loginStreak, setLoginStreak] = useState<LoginStreakData | null>(null);

  // ── Bet filters ─────────────────────────────────────────────────────────────
  const [filterSearch, setFilterSearch] = useState('');
  const [filterResult, setFilterResult] = useState<'all' | 'won' | 'lost' | 'pending'>('all');
  const [filterBookmaker, setFilterBookmaker] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const { user, plan, openPaywallModal, supabaseUser, isRegistered, openLoginModal, isPremium, isAdmin, avatarUrl } = useAuth();

  const displayName = user?.username ?? 'Mi Perfil';
  const displayPlan = plan ?? 'free';
  const planLabel = displayPlan === 'premium' ? 'Premium' : displayPlan === 'elite' ? 'Elite' : 'Gratis';
  const planColor =
    displayPlan === 'elite'
      ? 'bg-[#ffd700]/15 text-[#ffd700]'
      : displayPlan === 'premium'
      ? 'bg-[#00e87a]/15 text-[#00e87a]'
      : 'bg-[#a8b2c1]/15 text-[#a8b2c1]';

  // ── Computed bankroll stats from real bets ──────────────────────────────────
  const closedBets = bets.filter((b) => b.result !== 'pending');
  const wonBets = closedBets.filter((b) => b.result === 'won');
  const totalStake = closedBets.reduce((acc, b) => acc + b.stake, 0);
  const totalProfit = bets.reduce((acc, b) => acc + b.profit, 0);
  const roi = totalStake > 0 ? ((totalProfit / totalStake) * 100).toFixed(1) : '0.0';
  const winRate = closedBets.length > 0 ? Math.round((wonBets.length / closedBets.length) * 100) : 0;
  const avgOdds = bets.length > 0 ? (bets.reduce((acc, b) => acc + b.odds, 0) / bets.length).toFixed(2) : '0.00';
  const avgStake = bets.length > 0 ? (bets.reduce((acc, b) => acc + b.stake, 0) / bets.length).toFixed(2) : '0.00';

  // Best/worst streak from bets
  const { bestStreak, worstStreak } = (() => {
    let best = 0, worst = 0, cur = 0;
    for (const b of [...bets].reverse()) {
      if (b.result === 'won') { cur = cur >= 0 ? cur + 1 : 1; }
      else if (b.result === 'lost') { cur = cur <= 0 ? cur - 1 : -1; }
      if (cur > best) best = cur;
      if (cur < worst) worst = cur;
    }
    return { bestStreak: best, worstStreak: Math.abs(worst) };
  })();

  // Bankroll history from bets (cumulative profit over time)
  const bankrollHistory = (() => {
    const sorted = [...bets].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let running = 0;
    const points: { date: string; value: number }[] = [{ date: 'Inicio', value: 0 }];
    for (const b of sorted) {
      running += b.profit;
      points.push({ date: b.date, value: parseFloat(running.toFixed(2)) });
    }
    return points;
  })();

  // Monthly performance from real bets
  const monthlyPerformance = (() => {
    const map: Record<string, { roi: number; profit: number; bets: number; won: number; stake: number }> = {};
    for (const b of bets) {
      const d = new Date(b.bet_date ?? b.date);
      const key = d.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
      if (!map[key]) map[key] = { roi: 0, profit: 0, bets: 0, won: 0, stake: 0 };
      map[key].bets++;
      map[key].profit += b.profit;
      map[key].stake += b.stake;
      if (b.result === 'won') map[key].won++;
    }
    return Object.entries(map).map(([month, v]) => ({
      month,
      roi: v.stake > 0 ? parseFloat(((v.profit / v.stake) * 100).toFixed(1)) : 0,
      profit: parseFloat(v.profit.toFixed(2)),
      bets: v.bets,
      won: v.won,
    }));
  })();

  // ── Unique bookmakers for filter dropdown ───────────────────────────────────
  const uniqueBookmakers = useMemo(() => {
    const set = new Set(bets.map((b) => b.bookmaker).filter(Boolean));
    return Array.from(set).sort();
  }, [bets]);

  // ── Filtered bets ───────────────────────────────────────────────────────────
  const filteredBets = useMemo(() => {
    return bets.filter((b) => {
      if (filterResult !== 'all' && b.result !== filterResult) return false;
      if (filterBookmaker !== 'all' && b.bookmaker !== filterBookmaker) return false;
      if (filterSearch.trim()) {
        const q = filterSearch.toLowerCase();
        if (!b.match.toLowerCase().includes(q) && !b.market.toLowerCase().includes(q) && !b.selection.toLowerCase().includes(q)) return false;
      }
      if (filterDateFrom && b.bet_date && b.bet_date < filterDateFrom) return false;
      if (filterDateTo && b.bet_date && b.bet_date > filterDateTo) return false;
      return true;
    });
  }, [bets, filterResult, filterBookmaker, filterSearch, filterDateFrom, filterDateTo]);

  const hasActiveFilters = filterResult !== 'all' || filterBookmaker !== 'all' || filterSearch.trim() !== '' || filterDateFrom !== '' || filterDateTo !== '';

  const clearFilters = () => {
    setFilterResult('all');
    setFilterBookmaker('all');
    setFilterSearch('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const fetchBets = async () => {
    if (!supabaseUser) return;
    setBetsLoading(true);
    const { data } = await supabase
      .from('bets')
      .select('*')
      .eq('user_id', supabaseUser.id)
      .order('created_at', { ascending: false });
    setBets((data as SupabaseBet[] ?? []).map(supabaseBetToBetEntry));
    setBetsLoading(false);
  };

  const fetchPosts = async () => {
    if (!supabaseUser) return;
    setPostsLoading(true);
    const { data } = await supabase
      .from('posts')
      .select('id, title, content, category, category_label, is_prediction, prediction_odds, prediction_market, prediction_result, likes_count, views, created_at')
      .eq('user_id', supabaseUser.id)
      .order('created_at', { ascending: false });
    setUserPosts((data as PostEntry[]) ?? []);
    setPostsLoading(false);
  };

  const fetchStreak = async () => {
    if (!supabaseUser) return;
    const { data } = await supabase
      .from('login_streaks')
      .select('current_streak, longest_streak, total_logins')
      .eq('user_id', supabaseUser.id)
      .maybeSingle();
    if (data) setLoginStreak(data as LoginStreakData);
  };

  useEffect(() => {
    if (!supabaseUser) return;
    supabase
      .from('profiles')
      .select('hide_stats_leagues')
      .eq('id', supabaseUser.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setHideStatsLeagues(data.hide_stats_leagues ?? false);
      });
  }, [supabaseUser]);

  const handleToggleHideStats = async () => {
    if (!supabaseUser) return;
    const newVal = !hideStatsLeagues;
    setUpdatingHideStats(true);
    await supabase.from('profiles').update({ hide_stats_leagues: newVal }).eq('id', supabaseUser.id);
    setHideStatsLeagues(newVal);
    setUpdatingHideStats(false);
  };

  useEffect(() => {
    if (!supabaseUser) return;
    fetchBets();
    fetchPosts();
    fetchStreak();
    const channel = supabase
      .channel('own-posts-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts', filter: `user_id=eq.${supabaseUser.id}` }, () => { fetchPosts(); })
      .subscribe();
    const betsChannel = supabase
      .channel('own-bets-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bets', filter: `user_id=eq.${supabaseUser.id}` }, () => { fetchBets(); })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(betsChannel);
    };
  }, [supabaseUser]);

  const handleUpdatePickResult = async (postId: string, newResult: 'won' | 'lost' | 'pending') => {
    setUpdatingPick(postId);
    await supabase.from('posts').update({ prediction_result: newResult }).eq('id', postId);
    setUpdatingPick(null);
  };

  const handleDeletePost = async (postId: string) => {
    setDeletingPost(postId);
    await supabase.from('posts').delete().eq('id', postId);
    setDeletingPost(null);
    setConfirmDeletePostId(null);
  };

  const handleUpdateBetResult = async (betId: string, newResult: 'won' | 'lost' | 'pending') => {
    setUpdatingBetResult(betId);
    const bet = bets.find((b) => b.id === betId);
    if (!bet) { setUpdatingBetResult(null); return; }
    const profit =
      newResult === 'won' ? parseFloat(((bet.odds - 1) * bet.stake).toFixed(2))
      : newResult === 'lost' ? -bet.stake
      : 0;
    await supabase.from('bets').update({ result: newResult, profit }).eq('id', betId);
    setUpdatingBetResult(null);
  };

  const handleConfirmDeleteBet = async () => {
    if (!confirmDeleteBetId) return;
    setDeletingBet(confirmDeleteBetId);
    await supabase.from('bets').delete().eq('id', confirmDeleteBetId);
    setDeletingBet(null);
    setConfirmDeleteBetId(null);
  };

  const handleNewBet = (bet: BetEntry) => {
    setBets((prev) => [bet, ...prev]);
    setTimeout(() => setActiveTab('bankroll'), 1400);
  };

  if (!isRegistered) {
    return (
      <div className="bg-[#0d0f14] min-h-screen">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#00e87a]/10 mx-auto mb-4">
              <i className="ri-user-line text-[#00e87a] text-2xl"></i>
            </div>
            <h2 className="text-white font-black text-xl mb-2">Accede a tu perfil</h2>
            <p className="text-[#6b7280] text-sm mb-6">
              Inicia sesión para ver tus estadísticas, historial y picks publicados.
            </p>
            <button
              onClick={openLoginModal}
              className="px-6 py-3 bg-[#00e87a] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#00d070] transition-all whitespace-nowrap"
            >
              Iniciar sesión
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <LoginStreakBanner />
      <EditProfileModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        currentName={displayName}
        currentAvatar=""
        onSave={() => {}}
      />
      <RegisterBetModal isOpen={registerBetOpen} onClose={() => setRegisterBetOpen(false)} onSave={handleNewBet} />
      <NotificationsModal isOpen={notifModalOpen} onClose={() => setNotifModalOpen(false)} userEmail={supabaseUser?.email ?? ''} />

      {/* Confirm delete POST modal */}
      {confirmDeletePostId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4">
          <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 max-w-sm w-full text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#ef4444]/15 mx-auto mb-4">
              <i className="ri-delete-bin-line text-[#ef4444] text-xl"></i>
            </div>
            <h3 className="text-white font-bold text-base mb-2">¿Eliminar este post?</h3>
            <p className="text-[#6b7280] text-sm mb-6">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeletePostId(null)} className="flex-1 py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-medium rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-all">Cancelar</button>
              <button
                onClick={() => handleDeletePost(confirmDeletePostId)}
                disabled={deletingPost === confirmDeletePostId}
                className="flex-1 py-2.5 bg-[#ef4444] text-white text-sm font-bold rounded-xl hover:bg-[#dc2626] cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {deletingPost === confirmDeletePostId ? <><i className="ri-loader-4-line text-sm animate-spin"></i>Eliminando...</> : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete BET modal */}
      {confirmDeleteBetId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4">
          <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 max-w-sm w-full text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#ef4444]/15 mx-auto mb-4">
              <i className="ri-delete-bin-line text-[#ef4444] text-xl"></i>
            </div>
            <h3 className="text-white font-bold text-base mb-2">¿Eliminar esta apuesta?</h3>
            <p className="text-[#6b7280] text-sm mb-6">Se eliminará del historial y afectará tus estadísticas. Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteBetId(null)} className="flex-1 py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-medium rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-all">Cancelar</button>
              <button
                onClick={handleConfirmDeleteBet}
                disabled={deletingBet === confirmDeleteBetId}
                className="flex-1 py-2.5 bg-[#ef4444] text-white text-sm font-bold rounded-xl hover:bg-[#dc2626] cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {deletingBet === confirmDeleteBetId ? <><i className="ri-loader-4-line text-sm animate-spin"></i>Eliminando...</> : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pt-16">
        {/* Profile header */}
        <div className="bg-[#151719] border-b border-[#2a2d3a]">
          <div className="max-w-[1440px] mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-[#2a2d3a] border-2 border-[#2a2d3a] overflow-hidden flex items-center justify-center">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover object-top" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <i className="ri-user-line text-[#6b7280] text-3xl"></i>
                  )}
                </div>
                <button onClick={() => setEditModalOpen(true)} className="absolute bottom-0 right-0 w-6 h-6 flex items-center justify-center bg-[#00e87a] rounded-full cursor-pointer hover:bg-[#00d070] transition-colors" title="Cambiar foto de perfil">
                  <i className="ri-camera-line text-black" style={{ fontSize: '11px' }}></i>
                </button>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-white text-2xl font-black">@{displayName}</h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${planColor}`}>{planLabel}</span>
                  {isAdmin && (
                    <span className="px-3 py-1 rounded-full text-xs font-black bg-[#ff6b35]/20 text-[#ff6b35] border border-[#ff6b35]/40 flex items-center gap-1">
                      <i className="ri-shield-star-line text-xs"></i>ADMIN
                    </span>
                  )}
                </div>
                <p className="text-[#6b7280] text-sm mt-1">{supabaseUser?.email ?? ''}</p>
                <div className="flex items-center gap-6 mt-3 flex-wrap">
                  {[
                    { label: 'Posts públicos', value: userPosts.length },
                    { label: 'Picks subidos', value: userPosts.filter((p) => p.is_prediction).length },
                    { label: 'Picks ganados', value: userPosts.filter((p) => p.prediction_result === 'won').length },
                    { label: 'Total likes', value: userPosts.reduce((acc, p) => acc + p.likes_count, 0) },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <p className="text-white font-bold">{s.value}</p>
                      <p className="text-[#6b7280] text-xs">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={() => setEditModalOpen(true)} className="px-4 py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-sm rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap">Editar Perfil</button>
                {isPremium && (
                  <button onClick={() => setNotifModalOpen(true)} className="px-4 py-2.5 border border-[#ffd700]/30 text-[#ffd700] text-sm rounded-xl hover:bg-[#ffd700]/10 cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-notification-3-line text-sm"></i>Alertas
                  </button>
                )}
                {plan === 'elite' && (
                  <>
                    <button
                      onClick={handleToggleHideStats}
                      disabled={updatingHideStats}
                      className={`px-4 py-2.5 border text-sm rounded-xl cursor-pointer whitespace-nowrap flex items-center gap-2 transition-all disabled:opacity-60 ${hideStatsLeagues ? 'border-[#ff6b35]/50 bg-[#ff6b35]/15 text-[#ff6b35]' : 'border-[#ff6b35]/30 text-[#ff6b35] hover:bg-[#ff6b35]/10'}`}
                    >
                      {updatingHideStats ? <i className="ri-loader-4-line text-sm animate-spin"></i> : <i className={`${hideStatsLeagues ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>}
                      {hideStatsLeagues ? 'Stats ocultas' : 'Ocultar stats'}
                    </button>
                    <button
                      onClick={() => exportToCSV(
                        bets.map((b) => ({ Fecha: b.date, Partido: b.match, Mercado: b.market, Selección: b.selection, Cuota: b.odds, Stake: `${b.stake}€`, Casa: b.bookmaker, Resultado: b.result, 'P/L': `${b.profit}€` })),
                        `apuesta_mente_bets_${new Date().toISOString().split('T')[0]}.csv`
                      )}
                      className="px-4 py-2.5 border border-[#ff6b35]/30 text-[#ff6b35] text-sm rounded-xl hover:bg-[#ff6b35]/10 cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-file-excel-line text-sm"></i>Exportar Excel
                    </button>
                  </>
                )}
                <button onClick={openPaywallModal} className="px-4 py-2.5 bg-[#00e87a] text-black text-sm font-bold rounded-xl hover:bg-[#00d070] cursor-pointer whitespace-nowrap flex items-center gap-2">
                  <i className="ri-vip-crown-line text-sm"></i>Actualizar plan
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {(() => {
              const picks = userPosts.filter((p) => p.is_prediction && p.prediction_result !== 'pending');
              const wonPicks = picks.filter((p) => p.prediction_result === 'won');
              const pickProfit = picks.reduce((acc, p) => {
                if (p.prediction_result === 'won') return acc + ((p.prediction_odds ?? 2) - 1);
                return acc - 1;
              }, 0);
              const pickRoi = picks.length > 0 ? ((pickProfit / picks.length) * 100).toFixed(1) : '0.0';
              const pickWinRate = picks.length > 0 ? Math.round((wonPicks.length / picks.length) * 100) : 0;
              return [
                { label: 'ROI Picks Públicos', value: `${Number(pickRoi) >= 0 ? '+' : ''}${pickRoi}%`, sub: `${picks.length} picks cerrados`, color: '#00e87a', icon: 'ri-percent-line' },
                { label: '% Acierto Picks', value: `${pickWinRate}%`, sub: `${wonPicks.length}/${picks.length} ganados`, color: '#ffd700', icon: 'ri-check-double-line' },
                { label: 'ROI Bankroll', value: `${Number(roi) >= 0 ? '+' : ''}${roi}%`, sub: `${bets.length} apuestas registradas`, color: '#ff6b35', icon: 'ri-line-chart-line' },
                { label: 'P/L Total', value: `${totalProfit >= 0 ? '+' : ''}${totalProfit.toFixed(2)}€`, sub: `${wonBets.length}/${closedBets.length} ganadas`, color: totalProfit >= 0 ? '#00e87a' : '#ef4444', icon: 'ri-money-euro-circle-line' },
              ];
            })().map((card) => (
              <div key={card.label} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[#6b7280] text-xs">{card.label}</p>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${card.color}15`, color: card.color }}>
                    <i className={`${card.icon} text-base`}></i>
                  </div>
                </div>
                <p className="text-white text-2xl font-black" style={{ color: card.color }}>{card.value}</p>
                <p className="text-[#6b7280] text-xs mt-1">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1 mb-6 w-fit flex-wrap">
            {[
              { key: 'bankroll', label: 'Bankroll Personal', icon: 'ri-line-chart-line' },
              { key: 'posts', label: 'Mis Posts', icon: 'ri-article-line' },
              { key: 'estadisticas', label: 'Estadísticas', icon: 'ri-bar-chart-2-line' },
              { key: 'suscripcion', label: 'Mi Suscripción', icon: 'ri-vip-crown-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key ? 'bg-[#00e87a]/15 text-[#00e87a]' : 'text-[#6b7280] hover:text-white'}`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`}></i>
                </div>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Bankroll tab */}
          {activeTab === 'bankroll' && (
            <div className="space-y-6">
              <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white font-bold">Evolución del Bankroll</h3>
                  <span className={`font-black text-lg ${totalProfit >= 0 ? 'text-[#00e87a]' : 'text-[#ef4444]'}`}>
                    {totalProfit >= 0 ? '+' : ''}{totalProfit.toFixed(2)}€
                  </span>
                </div>
                {betsLoading ? (
                  <div className="h-16 bg-[#0d0f14] rounded-xl animate-pulse"></div>
                ) : bankrollHistory.length > 1 ? (
                  <div className="bg-[#0d0f14] rounded-xl p-4 mb-4">
                    <MiniChart history={bankrollHistory} />
                    <div className="flex justify-between mt-2">
                      <span className="text-[#6b7280] text-xs">{bankrollHistory[0].date}</span>
                      <span className="text-[#6b7280] text-xs">{bankrollHistory[bankrollHistory.length - 1].date}</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#0d0f14] rounded-xl p-8 text-center mb-4">
                    <p className="text-[#6b7280] text-sm">Registra tu primera apuesta para ver la evolución</p>
                  </div>
                )}
                {monthlyPerformance.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {monthlyPerformance.map((m) => (
                      <div key={m.month} className="bg-[#0d0f14] rounded-xl p-3 text-center">
                        <p className="text-[#6b7280] text-xs">{m.month}</p>
                        <p className={`font-black text-lg ${m.roi >= 0 ? 'text-[#00e87a]' : 'text-[#ef4444]'}`}>
                          {m.roi >= 0 ? '+' : ''}{m.roi}%
                        </p>
                        <p className="text-[#6b7280] text-xs">{m.bets} apuestas</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bets table with filters */}
              <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-[#2a2d3a]">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-white font-bold">Mis Apuestas Registradas</h3>
                      <p className="text-[#6b7280] text-xs mt-0.5">
                        Solo tú puedes ver estas apuestas — Privado
                        {hasActiveFilters && (
                          <span className="ml-2 text-[#00e87a]">· {filteredBets.length} de {bets.length} apuestas</span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => setRegisterBetOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#00e87a]/10 border border-[#00e87a]/30 text-[#00e87a] text-sm font-bold rounded-xl hover:bg-[#00e87a]/20 cursor-pointer transition-all whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-add-circle-line text-sm"></i>
                      </div>
                      Nueva Apuesta
                    </button>
                  </div>

                  {/* Filters row */}
                  <div className="flex flex-wrap gap-3 items-center">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[180px]">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                        <i className="ri-search-line text-sm"></i>
                      </div>
                      <input
                        type="text"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        placeholder="Buscar partido, mercado..."
                        className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl pl-9 pr-4 py-2.5 focus:outline-none focus:border-[#00e87a]/40 transition-colors placeholder-[#3d4150]"
                      />
                    </div>

                    {/* Result filter */}
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                        <i className="ri-filter-3-line text-sm"></i>
                      </div>
                      <select
                        value={filterResult}
                        onChange={(e) => setFilterResult(e.target.value as typeof filterResult)}
                        className="bg-[#0d0f14] border border-[#2a2d3a] text-sm rounded-xl pl-9 pr-4 py-2.5 focus:outline-none focus:border-[#00e87a]/40 transition-colors cursor-pointer appearance-none text-white"
                      >
                        <option value="all">Todos los resultados</option>
                        <option value="won">Ganadas</option>
                        <option value="lost">Perdidas</option>
                        <option value="pending">Pendientes</option>
                      </select>
                    </div>

                    {/* Bookmaker filter */}
                    {uniqueBookmakers.length > 0 && (
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                          <i className="ri-store-2-line text-sm"></i>
                        </div>
                        <select
                          value={filterBookmaker}
                          onChange={(e) => setFilterBookmaker(e.target.value)}
                          className="bg-[#0d0f14] border border-[#2a2d3a] text-sm rounded-xl pl-9 pr-4 py-2.5 focus:outline-none focus:border-[#00e87a]/40 transition-colors cursor-pointer appearance-none text-white"
                        >
                          <option value="all">Todas las casas</option>
                          {uniqueBookmakers.map((bm) => (
                            <option key={bm} value={bm}>{bm}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Date range */}
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                          <i className="ri-calendar-line text-sm"></i>
                        </div>
                        <input
                          type="date"
                          value={filterDateFrom}
                          onChange={(e) => setFilterDateFrom(e.target.value)}
                          className="bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#00e87a]/40 transition-colors cursor-pointer"
                          title="Desde"
                        />
                      </div>
                      <span className="text-[#6b7280] text-xs">—</span>
                      <input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        className="bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#00e87a]/40 transition-colors cursor-pointer"
                        title="Hasta"
                      />
                    </div>

                    {/* Clear filters */}
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="flex items-center gap-1.5 px-3 py-2.5 text-[#ef4444] text-sm rounded-xl border border-[#ef4444]/30 hover:bg-[#ef4444]/10 cursor-pointer transition-all whitespace-nowrap"
                      >
                        <i className="ri-close-circle-line text-sm"></i>
                        Limpiar
                      </button>
                    )}
                  </div>
                </div>

                {bets.length === 0 ? (
                  <div className="p-10 text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#2a2d3a] mx-auto mb-3">
                      <i className="ri-inbox-line text-[#6b7280] text-xl"></i>
                    </div>
                    <p className="text-[#6b7280] text-sm">Aún no has registrado ninguna apuesta</p>
                  </div>
                ) : filteredBets.length === 0 ? (
                  <div className="p-10 text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#2a2d3a] mx-auto mb-3">
                      <i className="ri-search-line text-[#6b7280] text-xl"></i>
                    </div>
                    <p className="text-[#6b7280] text-sm">No hay apuestas con los filtros aplicados</p>
                    <button onClick={clearFilters} className="mt-3 text-[#00e87a] text-sm cursor-pointer hover:underline">Limpiar filtros</button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead>
                        <tr className="border-b border-[#1e2028]">
                          {['Fecha', 'Partido', 'Mercado', 'Cuota', 'Stake', 'Casa', 'Resultado', 'P/L', ''].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-[#6b7280] text-xs uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBets.map((bet) => (
                          <tr key={bet.id} className="border-b border-[#1e2028] hover:bg-white/2">
                            <td className="px-4 py-3 text-[#6b7280] text-xs whitespace-nowrap">{bet.date}</td>
                            <td className="px-4 py-3 text-white text-xs font-medium whitespace-nowrap">{bet.match}</td>
                            <td className="px-4 py-3 text-[#a8b2c1] text-xs">{bet.market}</td>
                            <td className="px-4 py-3 text-white text-sm font-bold">{bet.odds.toFixed(2)}</td>
                            <td className="px-4 py-3 text-[#a8b2c1] text-xs">{bet.stake}€</td>
                            <td className="px-4 py-3 text-[#6b7280] text-xs">{bet.bookmaker}</td>
                            <td className="px-4 py-3">
                              {bet.result === 'pending' ? (
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleUpdateBetResult(bet.id, 'won')}
                                    disabled={updatingBetResult === bet.id}
                                    className="px-2 py-1 bg-[#00e87a]/10 border border-[#00e87a]/30 text-[#00e87a] text-xs font-bold rounded-lg cursor-pointer hover:bg-[#00e87a]/20 whitespace-nowrap"
                                  >G</button>
                                  <button
                                    onClick={() => handleUpdateBetResult(bet.id, 'lost')}
                                    disabled={updatingBetResult === bet.id}
                                    className="px-2 py-1 bg-[#ef4444]/10 border border-[#ef4444]/30 text-[#ef4444] text-xs font-bold rounded-lg cursor-pointer hover:bg-[#ef4444]/20 whitespace-nowrap"
                                  >P</button>
                                </div>
                              ) : (
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${bet.result === 'won' ? 'bg-[#00e87a]/15 text-[#00e87a]' : 'bg-[#ef4444]/15 text-[#ef4444]'}`}>
                                  {bet.result === 'won' ? 'GANADA' : 'PERDIDA'}
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-sm font-black ${bet.profit > 0 ? 'text-[#00e87a]' : bet.profit < 0 ? 'text-[#ef4444]' : 'text-[#ffd700]'}`}>
                              {bet.profit > 0 ? '+' : ''}{bet.profit.toFixed(2)}€
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setConfirmDeleteBetId(bet.id)}
                                className="w-6 h-6 flex items-center justify-center text-[#6b7280] hover:text-[#ef4444] rounded cursor-pointer transition-colors"
                              >
                                <i className="ri-delete-bin-line text-xs"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Posts tab */}
          {activeTab === 'posts' && (
            <div className="space-y-4">
              {postsLoading && (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5 animate-pulse">
                      <div className="h-4 bg-[#2a2d3a] rounded w-1/2 mb-3"></div>
                      <div className="h-3 bg-[#2a2d3a] rounded w-full mb-2"></div>
                      <div className="h-3 bg-[#2a2d3a] rounded w-3/4"></div>
                    </div>
                  ))}
                </div>
              )}
              {!postsLoading && userPosts.length === 0 && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-14 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#00e87a]/10 mx-auto mb-4">
                    <i className="ri-article-line text-[#00e87a] text-2xl"></i>
                  </div>
                  <p className="text-white font-bold text-lg mb-2">Aún no has publicado nada</p>
                  <p className="text-[#6b7280] text-sm mb-5">Ve a la comunidad y comparte tu primer análisis o pick.</p>
                  <a href="/comunidad" className="inline-block px-5 py-2.5 bg-[#00e87a] text-black text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap">Ir a la Comunidad</a>
                </div>
              )}
              {!postsLoading && userPosts.map((post) => (
                <div key={post.id} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5 hover:border-[#00e87a]/20 transition-all">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#00e87a]/10 text-[#00e87a]">{post.category_label}</span>
                      {post.is_prediction && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#ffd700]/10 text-[#ffd700]">PICK</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#6b7280] text-xs whitespace-nowrap">{new Date(post.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      <button onClick={() => setConfirmDeletePostId(post.id)} className="w-7 h-7 flex items-center justify-center text-[#6b7280] hover:text-[#ef4444] hover:bg-[#ef4444]/10 rounded-lg transition-all cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{post.title}</h3>
                  <p className="text-[#a8b2c1] text-sm leading-relaxed line-clamp-3">{post.content}</p>
                  {post.is_prediction && (
                    <div className="mt-4 p-4 bg-[#0d0f14] border border-[#2a2d3a] rounded-xl">
                      <div className="flex items-center gap-3 flex-wrap mb-3">
                        {post.prediction_market && <span className="text-[#6b7280] text-xs">{post.prediction_market}</span>}
                        {post.prediction_odds && <span className="text-white font-bold text-sm">@{Number(post.prediction_odds).toFixed(2)}</span>}
                        <span className={`px-2.5 py-1 rounded text-xs font-bold ${post.prediction_result === 'won' ? 'bg-[#00e87a]/15 text-[#00e87a]' : post.prediction_result === 'lost' ? 'bg-[#ef4444]/15 text-[#ef4444]' : 'bg-[#ffd700]/15 text-[#ffd700]'}`}>
                          {post.prediction_result === 'won' ? '✓ GANADO' : post.prediction_result === 'lost' ? '✗ PERDIDO' : '⏳ PENDIENTE'}
                        </span>
                      </div>
                      {post.prediction_result === 'pending' && (
                        <div className="flex items-center gap-2">
                          <p className="text-[#6b7280] text-xs mr-1">Verificar resultado:</p>
                          <button onClick={() => handleUpdatePickResult(post.id, 'won')} disabled={updatingPick === post.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00e87a]/10 border border-[#00e87a]/30 text-[#00e87a] text-xs font-bold rounded-lg hover:bg-[#00e87a]/20 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50">
                            {updatingPick === post.id ? <i className="ri-loader-4-line text-xs animate-spin"></i> : <i className="ri-check-double-line text-xs"></i>}Ganado
                          </button>
                          <button onClick={() => handleUpdatePickResult(post.id, 'lost')} disabled={updatingPick === post.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#ef4444]/10 border border-[#ef4444]/30 text-[#ef4444] text-xs font-bold rounded-lg hover:bg-[#ef4444]/20 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50">
                            {updatingPick === post.id ? <i className="ri-loader-4-line text-xs animate-spin"></i> : <i className="ri-close-circle-line text-xs"></i>}Perdido
                          </button>
                        </div>
                      )}
                      {(post.prediction_result === 'won' || post.prediction_result === 'lost') && (
                        <button onClick={() => handleUpdatePickResult(post.id, 'pending')} className="flex items-center gap-1.5 text-[#6b7280] text-xs hover:text-[#a8b2c1] cursor-pointer transition-all mt-1">
                          <i className="ri-arrow-go-back-line text-xs"></i>Marcar como pendiente
                        </button>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-4 text-[#6b7280] text-xs">
                    <span className="flex items-center gap-1"><i className="ri-heart-line text-xs"></i> {post.likes_count} likes</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line text-xs"></i> {post.views} vistas</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats tab */}
          {activeTab === 'estadisticas' && (
            <div className="space-y-6">
              {bets.length === 0 ? (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#a8b2c1]/10 mx-auto mb-4">
                    <i className="ri-bar-chart-2-line text-[#a8b2c1] text-2xl"></i>
                  </div>
                  <p className="text-white font-bold text-lg mb-2">Sin datos todavía</p>
                  <p className="text-[#6b7280] text-sm">Registra apuestas en el Bankroll Personal para ver tus estadísticas reales.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    { label: 'Cuota media apostada', value: avgOdds, color: '#ffd700' },
                    { label: 'Stake medio por apuesta', value: `${avgStake}€`, color: '#ff6b35' },
                    { label: 'Mejor racha ganadora', value: `+${bestStreak} picks`, color: '#00e87a' },
                    { label: 'Peor racha perdedora', value: `${worstStreak} picks`, color: '#ef4444' },
                    { label: 'Total apostado', value: `${totalStake.toFixed(2)}€`, color: '#a8b2c1' },
                    { label: 'Beneficio neto', value: `${totalProfit >= 0 ? '+' : ''}${totalProfit.toFixed(2)}€`, color: totalProfit >= 0 ? '#00e87a' : '#ef4444' },
                    { label: 'Apuestas ganadas', value: `${wonBets.length} / ${closedBets.length}`, color: '#00e87a' },
                    { label: 'Win Rate', value: `${winRate}%`, color: '#ffd700' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5">
                      <p className="text-[#6b7280] text-sm mb-2">{stat.label}</p>
                      <p className="font-black text-3xl" style={{ color: stat.color }}>{stat.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Achievements */}
              <AchievementBadges
                bets={bets}
                streak={loginStreak}
                postsCount={userPosts.length}
              />
            </div>
          )}

          {activeTab === 'suscripcion' && <SubscriptionSection />}
        </div>
      </div>
      <Footer />
    </div>
  );
}
